import React, { useEffect, useState, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  Title,
  CategoryScale,
} from "chart.js";
import "chartjs-adapter-date-fns";
import ToolTip from "./ToolTip";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import { format } from "date-fns";

ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  Title,
  CategoryScale
)

function LineChart({ graphSwitch, resdata }) {
  const [tooltipModel, setTooltipModel] = useState({});
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [renderdata, setRenderData] = useState({ labels: [], datasets: [] });
console.log('resdata in linegraph',resdata)
  const chartRef = useRef();

  // ✅ Dataset styles
  const rawdataSystolic = {
    fill: false,
    lineTension: 0.3,
    backgroundColor: "#f84c1e",
    borderColor: "#f84c1e",
    borderCapStyle: "round",
    borderJoinStyle: "round",
    pointBorderColor: "#f84c1e",
    pointBackgroundColor: "#f84c1e",
    pointBorderWidth: 1,
    pointHoverRadius: 5,
    pointHoverBackgroundColor: "#f84c1e",
    pointHoverBorderColor: "#fff",
    pointHoverBorderWidth: 5,
    pointRadius: 4,
    pointHitRadius: 6,
  };

  const rawdataDiastolic = {
    fill: false,
    lineTension: 0.3,
    backgroundColor: "#2a2c30",
    borderColor: "#2a2c30",
    borderCapStyle: "round",
    borderJoinStyle: "round",
    pointBorderColor: "#2a2c30",
    pointBackgroundColor: "#2a2c30",
    pointBorderWidth: 1,
    pointHoverRadius: 5,
    pointHoverBackgroundColor: "#2a2c30",
    pointHoverBorderColor: "#fff",
    pointHoverBorderWidth: 5,
    pointRadius: 4,
    pointHitRadius: 6,
  };

  const rawdataPulse = {
    fill: false,
    lineTension: 0.3,
    backgroundColor: "#28a745",
    borderColor: "#28a745",
    borderCapStyle: "round",
    borderJoinStyle: "round",
    pointBorderColor: "#28a745",
    pointBackgroundColor: "#28a745",
    pointBorderWidth: 1,
    pointHoverRadius: 5,
    pointHoverBackgroundColor: "#28a745",
    pointHoverBorderColor: "#fff",
    pointHoverBorderWidth: 5,
    pointRadius: 4,
    pointHitRadius: 6,
  };

  // ✅ Extract years and set default to most recent
  useEffect(() => {
    if (resdata?.datelabel) {
      const years = new Set();
      resdata.datelabel.forEach((lbl) => {
        const d = new Date(lbl);
        if (!isNaN(d)) years.add(d.getFullYear());
      });

      const sortedYears = Array.from(years).sort((a, b) => b - a);
      setAvailableYears(sortedYears);

      if (sortedYears.length > 0) {
        setSelectedYear(sortedYears[0].toString());
      }
    }
  }, [resdata]);

// ✅ Prepare chart data - show each reading as individual node with real date spacing
useEffect(() => {
  if (!resdata || !resdata.datelabel || !selectedYear) return;

  const filteredIndices = resdata.datelabel
    .map((lbl, idx) => {
      const d = new Date(lbl);
      return !isNaN(d) && d.getFullYear().toString() === selectedYear ? idx : null;
    })
    .filter((idx) => idx !== null);

  filteredIndices.sort(
    (a, b) => new Date(resdata.datelabel[a]) - new Date(resdata.datelabel[b])
  );

  const allDates = filteredIndices.map((i) => new Date(resdata.datelabel[i]));

  const uniqueMonthDates = Array.from(
    new Set(allDates.map(d => new Date(d.getFullYear(), d.getMonth(), 1)))
  ).sort((a, b) => a - b);

  const updatedData = {
    labels: allDates,
    uniqueMonthDates, // store for x-axis ticks
    datasets: [],
  };

  const addDataset = (label, data, style) => {
    if (!data) return;
    updatedData.datasets.push({
      ...style,
      label,
      data: filteredIndices.map((i) => ({
        x: new Date(resdata.datelabel[i]),
        y: data[i],
      })),
      spanGaps: false,
      pointRadius: 4,
    });
  };

  if (graphSwitch.Systolic) addDataset("Systolic", resdata.systolic, rawdataSystolic);
  if (graphSwitch.Diastolic) addDataset("Diastolic", resdata.diastolic, rawdataDiastolic);
  if (graphSwitch.Pulse) addDataset("Pulse", resdata.pulse, rawdataPulse);

  setRenderData(updatedData);
}, [graphSwitch, resdata, selectedYear]);



const options = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: {
      type: "time",
      time: {
        unit: "month",
        displayFormats: { month: "MMMM" },
      },
      grid: { display: false },
      border: { display: false },
      ticks: {
        source: 'data',
        autoSkip: true,
        maxRotation: 0,
        minRotation: 0,
        callback: function (value, index, ticks) {
          const tickDate = new Date(value);
          if (isNaN(tickDate)) return "";
          // Show only the month name for the first occurrence
          return index === 0 || new Date(ticks[index - 1]).getMonth() !== tickDate.getMonth() 
            ? tickDate.toLocaleString("default", { month: "long" }) 
            : "";
        },
      },
    },
    y: {
      ticks: { stepSize: 10 },
      grid: { display: false },
      border: { display: false },
    },
  },
  plugins: {
    legend: { display: true, position: "bottom" },
    tooltip: {
      enabled: !true,
      mode: "nearest",
      intersect: false,
      callbacks: {
        title: (tooltipItems) => {
          const date = tooltipItems[0].parsed.x;
          return format(new Date(date), "MMM d, yyyy hh:mm a");
        },
        label: (tooltipItem) => {
          return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`;
        },
      },
 external: function(context) {
  const { chart, tooltip } = context;
  const container = chart.canvas.parentNode;

  let tooltipEl = container.querySelector("#chartjs-tooltip");
  if (!tooltipEl) {
    tooltipEl = document.createElement("div");
    tooltipEl.id = "chartjs-tooltip";
    tooltipEl.style.position = "absolute";
    tooltipEl.style.pointerEvents = "none";
    tooltipEl.style.borderRadius = "6px";
    tooltipEl.style.backgroundColor = "#fff"; // your bg color
    tooltipEl.style.color = "black";
    tooltipEl.style.padding = "6px 10px";
    tooltipEl.style.fontSize = "0.875rem";
    tooltipEl.style.transition = "all 0.05s ease";
    tooltipEl.style.whiteSpace = "nowrap";
    container.appendChild(tooltipEl);
  }

  // Hide if tooltip is hidden
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }

  // Set content
  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map(b => b.lines).flat();
    tooltipEl.innerHTML = `
      ${titleLines.map(line => `<div class="font-bold">${line}</div>`).join('')}
      ${bodyLines.map(line => `<div>${line}</div>`).join('')}
    `;
  }

  // Position tooltip exactly under point
  const canvasRect = chart.canvas.getBoundingClientRect();
  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = chart.canvas.offsetLeft + tooltip.caretX - tooltipEl.offsetWidth / 2 + "px";
  tooltipEl.style.top = chart.canvas.offsetTop + tooltip.caretY + 10 + "px"; // 10px below point
}

    },
  },
};



  // ✅ Download Handlers
  const handleDownload = (type) => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;

    const ctx = tempCanvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(canvas, 0, 0);

    const link = document.createElement("a");
    link.download = `chart.${type}`;
    link.href = tempCanvas.toDataURL(`image/${type}`);
    link.click();
    setModalOpen(false);
  };

  const handlePDFDownload = () => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;
    const pdf = new jsPDF({
      orientation: canvas.width > canvas.height ? "landscape" : "portrait",
      unit: "pt",
      format: [canvas.width, canvas.height],
    });
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, canvas.width, canvas.height);
    pdf.save("chart.pdf");
    setModalOpen(false);
  };

  const handleSVGDownload = () => {
    if (!chartRef.current) return;
    const canvas = chartRef.current.canvas;
    const imgData = canvas.toDataURL("image/png");
    const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}"><image href="${imgData}" height="${canvas.height}" width="${canvas.width}" /></svg>`;
    const blob = new Blob([svgContent], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "chart.svg";
    link.click();
    setModalOpen(false);
  };

  const handleCSVDownload = () => {
    const csv = ["Label," + renderdata.datasets.map((ds) => ds.label).join(",")];
    const rowCount = renderdata.labels.length;
    for (let i = 0; i < rowCount; i++) {
      const row = [renderdata.labels[i]];
      renderdata.datasets.forEach((ds) => row.push(ds.data[i] ?? ""));
      csv.push(row.join(","));
    }
    const blob = new Blob([csv.join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "chart-data.csv";
    link.click();
    setModalOpen(false);
  };

  const handleXLSDownload = () => {
    const wsData = [
      ["Label", ...renderdata.datasets.map((ds) => ds.label)],
      ...renderdata.labels.map((label, i) => [
        label,
        ...renderdata.datasets.map((ds) => ds.data[i]),
      ]),
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Chart Data");
    XLSX.writeFile(workbook, "chart-data.xlsx");
    setModalOpen(false);
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        minHeight: "300px",
        maxHeight: "50vh",
        position: "relative",
      }}
    >
      {/* ✅ Top bar with Year Selector and Menu */}
      <div className="flex justify-between items-center px-4 ">
        {/* Year Selector */}
        <div className="relative inline-block mb-2 min-w-[120px]">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="
              w-full
              appearance-none
              bg-[#F9FAFB]
              text-[#0B1C33]
              border border-[#E4E4E7]
              rounded-md
              px-3 py-1
              text-sm
              font-medium
              focus:outline-none
              cursor-pointer
            "
          >
            {availableYears.map((year) => (
              <option key={year} value={year} className="bg-white text-[#0B1C33]">
                {year}
              </option>
            ))}
          </select>

          <svg
            className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none text-[#6B7280]"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6 8l4 4 4-4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <button
          className="text-gray-600 hover:text-black"
          onClick={() => setModalOpen(true)}
        >
          ☰
        </button>
      </div>

      {/* ✅ Chart */}
      <Line ref={chartRef} options={options} data={renderdata} />


      {/* ✅ Modal for downloads */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white text-sm rounded shadow-lg w-[90%] max-w-[512px]">
            <div className="flex justify-end px-2 pt-2">
              <button
                className="text-gray-600 hover:text-black text-lg font-bold"
                onClick={() => setModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="flex flex-wrap justify-start px-4 py-2 gap-4 text-blue-600">
              <button onClick={() => handleDownload("png")} className="hover:underline">
                Download PNG
              </button>
              <button onClick={() => handleDownload("jpeg")} className="hover:underline">
                Download JPEG
              </button>
              <button onClick={handlePDFDownload} className="hover:underline">
                Download PDF
              </button>
              <button onClick={handleSVGDownload} className="hover:underline">
                Download SVG
              </button>
              <button onClick={handleCSVDownload} className="hover:underline">
                Download CSV
              </button>
              <button onClick={handleXLSDownload} className="hover:underline">
                Download XLS
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LineChart;
